/**
 * RBAC Service
 * Frontend service for role-based access control API calls
 */

import axios from "axios";
import webAuthService from "./webAuthService";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8080";

/**
 * Get current user's role and permissions
 *
 * @returns {Promise<Object>} Role data with permissions
 * @throws {Error} If request fails
 */
export const getUserRole = async () => {
  try {
    // Get access token from MSAL
    const tokenResponse = await webAuthService.getAccessToken();

    if (!tokenResponse || !tokenResponse.accessToken) {
      throw new Error("No access token available. Please log in.");
    }

    const token = tokenResponse.accessToken;

    const response = await axios.get(`${API_BASE_URL}/auth/role`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (response.data.status === "success") {
      return {
        user_id: response.data.user_id,
        role: response.data.role,
        permissions: response.data.permissions,
      };
    } else {
      throw new Error(response.data.error || "Failed to fetch role");
    }
  } catch (error) {
    console.error("Error fetching user role:", error);

    // Handle specific error cases
    if (error.response) {
      // Server responded with error
      if (error.response.status === 401) {
        throw new Error("Authentication required. Please log in again.");
      } else if (error.response.status === 500) {
        // Backend may return fail-safe normal role on error
        if (error.response.data && error.response.data.role) {
          return {
            role: error.response.data.role,
            permissions: error.response.data.permissions,
          };
        }
      }
      throw new Error(error.response.data?.error || "Failed to fetch role");
    } else if (error.request) {
      // No response received
      throw new Error("Network error. Please check your connection.");
    } else {
      // Other errors
      throw error;
    }
  }
};

export default {
  getUserRole,
};
